import {
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { AgendaDetailedHeaderComponent } from './agenda-detailled-header/agenda-detailed-header.component';
import { AgendaDetailedService } from './agenda-detailed-service';
import { AgendaDetailedRowComponent } from './agenda-detailed-row/agenda-detailed-row.component';
import {
  CalendarClickEvent,
  CalendarDropTask,
  CalendarSelection,
  CalendarTask,
  CalendarTaskHeaderGroup,
  MenuDisplayService,
  Time,
} from '@infodata-group/inf-commons';
import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import { AgendaDetailedOptions } from './agenda-detailed-options';
import { AgendaDetailedRow } from './agenda-detailed-row';
import { Telework } from '../telework';

@Component({
  selector: 'infl-agenda-detailed-view',
  imports: [
    ScrollingModule,
    AgendaDetailedHeaderComponent,
    AgendaDetailedRowComponent,
  ],
  standalone: true,
  templateUrl: './agenda-detailed-view.html',
  styleUrl: './agenda-detailed-view.scss',
})
export class AgendaDetailedView {
  protected agendaDetailedService = inject(AgendaDetailedService);

  public date = input<Date>(new Date());
  public viewId = input<string>();
  public hoursSection = input<number>(null);
  public daySection = input<number>(null);
  public hourStart = input<Time>(null);
  public hourEndMorning = input<Time>(null);
  public hourStartAfternoon = input<Time>(null);
  public hourEnd = input<Time>(null);
  public tasks = input<CalendarTask[]>([]);
  public canResize = input(true);
  public canDrag = input(false);
  public canSelect = input(true);
  public resources = input<CalendarTaskHeaderGroup[]>([]);
  public outsideDraggingTask = input<CalendarDropTask<any>>(null);
  public sectionHeight = input(30);
  public fullRender = input(false);
  public teleworks = input<Telework[]>([]);
  public paginationStep = input(5);
  public currentPage = input(1);

  public periodHovered = output<CalendarSelection>();
  public periodSelected = output<CalendarSelection>();
  public taskClick = output<CalendarClickEvent>();
  public taskHover = output<CalendarTask>();
  public taskUpdate = output<CalendarTask>();
  public taskCreation = output<CalendarTask>();

  protected headerElement = viewChild<AgendaDetailedHeaderComponent>(
    AgendaDetailedHeaderComponent,
  );
  protected resourcesElement = viewChild<CdkVirtualScrollViewport>(
    CdkVirtualScrollViewport,
  );
  protected rows = viewChildren(AgendaDetailedRowComponent);

  public resourceWidth = signal(0);

  private init = true;
  private readonly displayMenuService = inject(MenuDisplayService);

  constructor() {
    this.displayMenuService.subscribe(() => {
      if (this.init) {
        this.init = false;
      } else {
        setTimeout(() => {
          const width = this.computeResourceWidth(
            this.resourcesElement(),
            this.resources(),
            this.paginationStep(),
            this.currentPage(),
          );
          this.resourceWidth.set(width);
        }, 500);
      }
    });
    effect(() => {
      const width = this.computeResourceWidth(
        this.resourcesElement(),
        this.resources(),
        this.paginationStep(),
        this.currentPage(),
      );
      this.resourceWidth.set(width);
    });
    effect(() => {
      if (this.outsideDraggingTask()) {
        this.agendaDetailedService.outsideDraggingTask =
          this.outsideDraggingTask();
      }
    });
    effect(() => {
      const options: AgendaDetailedOptions = {
        morningStart: this.hourStart(),
        morningEnd: this.hourEndMorning(),
        afternoonStart: this.hourStartAfternoon(),
        afternoonEnd: this.hourEnd(),
        hoursSection: this.hoursSection(),
        daySection: this.daySection(),
        canResize: this.canResize(),
        canDrag: this.canDrag(),
        canSelect: this.canSelect(),
        sectionHeight: this.sectionHeight(),
      };
      if (this.resources() && this.tasks()) {
        this.agendaDetailedService.groups = this.resources();
        this.agendaDetailedService.generateRows(
          this.date(),
          options,
          this.teleworks(),
        );
        this.agendaDetailedService.setTasks(this.tasks());
      }
    });
    effect(() => {
      if (this.resourcesElement() && this.date() && this.viewId()) {
        this.scrollToToday();
      }
    });
  }

  private computeResourceWidth(
    cdkVirtualScroll: CdkVirtualScrollViewport,
    resources: CalendarTaskHeaderGroup[],
    pagination: number,
    currentPage: number,
  ) {
    const widthAvailable =
      cdkVirtualScroll.elementRef.nativeElement.clientWidth - 180;
    const allResources = resources.flatMap((group) => group.tasksGroups);
    let nbResources = allResources.length;
    if (allResources.length > pagination) {
      nbResources = pagination;
      const indexResource = pagination * (currentPage + 1);
      if (indexResource >= allResources.length) {
        nbResources = allResources.length - pagination * currentPage;
      }
    }
    if (nbResources * 150 > widthAvailable) {
      return 150;
    }
    return widthAvailable / nbResources;
  }

  private scrollToToday() {
    let index = 0;
    if (this.date().getMonth() === new Date().getMonth()) {
      index = this.agendaDetailedService.rows.findIndex(
        (row: AgendaDetailedRow) => row.isCurrent,
      );
    }
    setTimeout(() => {
      this.resourcesElement().scrollToIndex(index, 'auto');
    }, 500);
  }

  protected onScrollResources($event: Event) {
    const target = $event.target as HTMLElement;
    const scrollLeft = target.scrollLeft;
    this.headerElement().scrollLeft(scrollLeft);
  }

  protected onScrollHeader($event: number) {
    const element = this.resourcesElement().elementRef.nativeElement;
    element.scrollLeft = $event;
  }

  deselect() {
    this.rows().forEach((row) => row.clearCells());
  }
}
